import { useState } from "react";
import { toast } from "sonner";
import { api, resolveUploadUrl } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import InvitationUploader from "@/components/InvitationUploader";
import {
  Megaphone,
  ExternalLink,
  Send,
  Building2,
  User as UserIcon,
  Phone,
  Globe,
  Sparkles,
  CheckCircle2,
  CreditCard,
  Copy,
  Image as ImageIcon,
} from "lucide-react";

const formatMoney = (n) =>
  new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

const emptyApp = {
  full_name: "",
  company_name: "",
  phone: "",
  website: "",
  package_label: "",
  message: "",
  logo_url: "",
};

const formatPhoneTR = (raw) => {
  const digits = (raw || "").replace(/\D/g, "").slice(0, 11);
  if (!digits) return "";
  let d = digits;
  if (d.length === 10 && d.startsWith("5")) d = "0" + d;
  const p = [];
  if (d.length > 0) p.push(d.slice(0, 1));
  if (d.length > 1) p.push(d.slice(1, 4));
  if (d.length > 4) p.push(d.slice(4, 7));
  if (d.length > 7) p.push(d.slice(7, 9));
  if (d.length > 9) p.push(d.slice(9, 11));
  return p.join(" ").trim();
};

const AdSpaceSection = () => {
  const { settings } = useSettings();
  const [applyOpen, setApplyOpen] = useState(false);
  const [form, setForm] = useState(emptyApp);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!settings.ad_enabled) return null;

  const activeAd =
    settings.ad_active_title || settings.ad_active_image_url
      ? {
          title: settings.ad_active_title,
          description: settings.ad_active_description,
          image_url: settings.ad_active_image_url,
          link: settings.ad_active_link,
        }
      : null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.full_name.trim() || !form.company_name.trim() || !form.phone.trim()) {
      toast.error("Ad, firma ve telefon zorunlu");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/ad-applications", form);
      setDone(true);
      toast.success("Başvurunuz alındı");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Başvuru gönderilemedi");
    } finally {
      setSubmitting(false);
    }
  };

  const closeAndReset = () => {
    setApplyOpen(false);
    setTimeout(() => {
      setForm(emptyApp);
      setDone(false);
    }, 300);
  };

  return (
    <section className="animate-fade-up" data-testid="ad-space-section">
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#8A252C]/80 font-bold">
            <Megaphone className="w-3.5 h-3.5" />
            Sponsor
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1F2937] mt-1">
            {settings.ad_section_title || "Reklam Alanı"}
          </h2>
          {settings.ad_section_description && (
            <p className="text-sm text-[#4B5563] mt-1 max-w-2xl">
              {settings.ad_section_description}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Active ad (or placeholder) */}
        <div className="lg:col-span-2">
          {activeAd ? (
            <a
              href={activeAd.link || "#"}
              target={activeAd.link ? "_blank" : undefined}
              rel="noopener noreferrer"
              data-testid="active-ad-card"
              className="block relative bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden hover:shadow-lg transition-all"
            >
              {activeAd.image_url && (
                <div className="aspect-[16/5] sm:aspect-[16/4] max-h-40 bg-white flex items-center justify-center overflow-hidden p-3">
                  <img
                    src={resolveUploadUrl(activeAd.image_url)}
                    alt={activeAd.title}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              )}
              <div className="p-5">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#8A252C]/80 font-bold">
                  <Sparkles className="w-3.5 h-3.5" />
                  Sponsor İçerik
                </div>
                <h3 className="font-display text-xl font-bold text-[#1F2937] mt-1">
                  {activeAd.title}
                </h3>
                {activeAd.description && (
                  <p className="text-sm text-[#4B5563] mt-1">{activeAd.description}</p>
                )}
                {activeAd.link && (
                  <div className="mt-3 inline-flex items-center gap-1 text-sm text-[#8A252C] font-semibold">
                    Ziyaret Et <ExternalLink className="w-3.5 h-3.5" />
                  </div>
                )}
              </div>
            </a>
          ) : (
            <div
              className="relative bg-white rounded-2xl border border-dashed border-[#D4AF37]/60 p-8 sm:p-10 text-center overflow-hidden"
              data-testid="ad-placeholder"
            >
              <div className="w-14 h-14 rounded-full bg-[#D4AF37]/15 flex items-center justify-center mx-auto">
                <Megaphone className="w-6 h-6 text-[#8A252C]" />
              </div>
              <h3 className="font-display text-2xl font-bold text-[#1F2937] mt-4">
                Bu alan sizin için ayrıldı
              </h3>
              <p className="text-sm text-[#4B5563] mt-1 max-w-md mx-auto">
                Firmanızı Şanlıurfa dernek üyelerine tanıtın. Uygun fiyatlı paketler ve şeffaf ödeme.
              </p>
              <Button
                onClick={() => setApplyOpen(true)}
                data-testid="ad-apply-btn-main"
                className="mt-5 bg-[#8A252C] hover:bg-[#6f1d24] text-white rounded-xl h-12 px-6 gap-2 shadow-sm"
              >
                <Sparkles className="w-4 h-4" />
                Reklam Ver
              </Button>
            </div>
          )}
        </div>

        {/* Packages + CTA */}
        <aside className="bg-white rounded-2xl border border-stone-200 shadow-sm p-5">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A252C]/80 font-bold">
            Paketler
          </div>
          <div className="mt-3 space-y-2">
            {(settings.ad_packages || []).length === 0 && (
              <p className="text-sm text-[#4B5563]">Şu anda tanımlı paket yok.</p>
            )}
            {(settings.ad_packages || []).map((p, i) => (
              <div
                key={i}
                data-testid={`ad-package-${i}`}
                className="flex items-center justify-between border border-stone-200 rounded-xl px-3 py-2.5"
              >
                <div>
                  <div className="font-display font-bold text-[#1F2937]">{p.label}</div>
                  <div className="text-xs text-[#4B5563]">{p.days} gün</div>
                </div>
                <div className="font-display text-lg font-bold text-[#8A252C]">
                  {formatMoney(p.price)}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-4 text-xs text-[#4B5563]">
            Ödeme bilgileri başvurunuz tamamlandıktan sonra ekranınıza gelecek.
          </p>

          <Button
            onClick={() => setApplyOpen(true)}
            data-testid="ad-apply-btn-side"
            className="mt-4 w-full bg-[#8A252C] hover:bg-[#6f1d24] text-white rounded-xl h-11 gap-2"
          >
            <Send className="w-4 h-4" />
            Reklam Ver
          </Button>
        </aside>
      </div>

      {/* Apply dialog */}
      <Dialog open={applyOpen} onOpenChange={(o) => (o ? setApplyOpen(true) : closeAndReset())}>
        <DialogContent
          className="max-w-lg bg-[#F9F6F0] border-stone-200 max-h-[92vh] overflow-y-auto"
          data-testid="ad-apply-dialog"
        >
          {done ? (
            <div className="text-center py-4" data-testid="ad-apply-success">
              <div className="w-16 h-16 rounded-full bg-[#4A5D23]/10 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-[#4A5D23]" />
              </div>
              <h3 className="font-display text-2xl font-bold text-[#1F2937] mt-3">
                Başvurunuz Alındı
              </h3>
              <p className="text-sm text-[#4B5563] mt-2 max-w-sm mx-auto">
                Aşağıdaki hesaba ödemenizi yaptıktan sonra dekont ile birlikte tarafımıza ulaşın.
                En kısa sürede sizinle iletişime geçilecek.
              </p>

              {(settings.ad_payment_bank ||
                settings.ad_payment_holder ||
                settings.ad_payment_iban) && (
                <div
                  className="mt-5 text-left bg-white rounded-2xl border border-stone-200 p-4 space-y-1.5"
                  data-testid="ad-payment-info"
                >
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A252C]/80 font-bold flex items-center gap-2">
                    <CreditCard className="w-3.5 h-3.5" />
                    Ödeme Bilgileri
                  </div>
                  {settings.ad_payment_bank && (
                    <PayRow label="Banka" value={settings.ad_payment_bank} />
                  )}
                  {settings.ad_payment_holder && (
                    <PayRow label="Hesap Sahibi" value={settings.ad_payment_holder} />
                  )}
                  {settings.ad_payment_iban && (
                    <PayRow label="IBAN" value={settings.ad_payment_iban} mono copyable />
                  )}
                  {settings.ad_payment_note && (
                    <p className="text-xs text-[#4B5563] italic mt-2">
                      {settings.ad_payment_note}
                    </p>
                  )}
                </div>
              )}

              <Button
                onClick={closeAndReset}
                data-testid="ad-apply-done-btn"
                className="mt-5 bg-[#8A252C] hover:bg-[#6f1d24] text-white rounded-xl h-11 px-6"
              >
                Kapat
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader className="text-left">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A252C]/80 font-bold">Sponsor</div>
                <DialogTitle className="font-display text-2xl text-[#1F2937]">
                  Reklam Başvurusu
                </DialogTitle>
                <p className="text-sm text-[#4B5563]">
                  Bilgilerinizi bırakın, yönetici en kısa sürede size dönüş yapsın.
                </p>
              </DialogHeader>

              <div className="space-y-4 py-2">
                <Field icon={UserIcon} label="Ad Soyad">
                  <Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)}
                    placeholder="Adınız Soyadınız" data-testid="ad-app-name-input"
                    className="bg-white border-stone-300 h-11" />
                </Field>
                <Field icon={Building2} label="Firma Adı">
                  <Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)}
                    placeholder="Firma / marka adı" data-testid="ad-app-company-input"
                    className="bg-white border-stone-300 h-11" />
                </Field>
                <Field icon={Phone} label="Telefon">
                  <Input value={form.phone} onChange={(e) => set("phone", formatPhoneTR(e.target.value))}
                    placeholder="0 5xx xxx xx xx" inputMode="tel" data-testid="ad-app-phone-input"
                    className="bg-white border-stone-300 h-11" />
                </Field>
                <Field icon={Globe} label="Web Sitesi (opsiyonel)">
                  <Input value={form.website} onChange={(e) => set("website", e.target.value)}
                    placeholder="https://..." inputMode="url" data-testid="ad-app-website-input"
                    className="bg-white border-stone-300 h-11" />
                </Field>
                {(settings.ad_packages || []).length > 0 && (
                  <Field icon={Sparkles} label="İlgilendiğiniz Paket (opsiyonel)">
                    <div className="flex flex-wrap gap-2">
                      {(settings.ad_packages || []).map((p, i) => {
                        const active = form.package_label === p.label;
                        return (
                          <button
                            key={i}
                            type="button"
                            onClick={() => set("package_label", active ? "" : p.label)}
                            data-testid={`ad-app-package-${i}`}
                            className={`px-3 h-9 rounded-full text-sm font-semibold border transition ${
                              active
                                ? "bg-[#8A252C] text-white border-[#8A252C]"
                                : "bg-white border-stone-300 text-[#4B5563] hover:border-[#8A252C]/50"
                            }`}
                          >
                            {p.label} · {formatMoney(p.price)}
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                )}
                <Field icon={Send} label="Mesaj (opsiyonel)">
                  <Textarea rows={3} value={form.message} onChange={(e) => set("message", e.target.value)}
                    placeholder="Kısa açıklama..." data-testid="ad-app-message-input"
                    className="bg-white border-stone-300 resize-none" />
                </Field>
                <Field icon={ImageIcon} label="Şirket Logosu / Kartvizit (opsiyonel)">
                  <InvitationUploader
                    value={form.logo_url}
                    onChange={(url) => set("logo_url", url)}
                    compact
                    title="Varsa şirketinize ait logo, kartvizit vb. yükleyin"
                    hint="PNG, JPG, WebP · en fazla 10 MB · opsiyonel"
                    doneLabel="Görsel yüklendi"
                  />
                </Field>
              </div>

              <DialogFooter className="gap-2 flex-col sm:flex-row">
                <Button variant="outline" onClick={closeAndReset} className="border-stone-300 h-11" data-testid="ad-app-cancel-btn">
                  Vazgeç
                </Button>
                <Button onClick={submit} disabled={submitting} data-testid="ad-app-submit-btn"
                  className="bg-[#8A252C] hover:bg-[#6f1d24] text-white h-11 gap-2">
                  <Send className="w-4 h-4" />
                  {submitting ? "Gönderiliyor..." : "Reklam Ver"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

const Field = ({ icon: Icon, label, children }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold flex items-center gap-1.5">
      {Icon && <Icon className="w-3 h-3" />}
      {label}
    </Label>
    <div className="mt-1">{children}</div>
  </div>
);

const PayRow = ({ label, value, mono, copyable }) => {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Panoya kopyalandı");
    } catch {
      toast.error("Kopyalanamadı");
    }
  };
  return (
    <div className="flex items-center gap-2">
      <div className="w-28 shrink-0 text-[10px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">
        {label}
      </div>
      <div className={`flex-1 min-w-0 break-all text-sm text-[#1F2937] ${mono ? "font-mono tracking-wider" : "font-semibold"}`}>
        {value}
      </div>
      {copyable && (
        <button
          type="button"
          onClick={copy}
          className="text-[#8A252C] hover:bg-[#8A252C]/5 p-1 rounded"
          title="Kopyala"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

export default AdSpaceSection;
